import { html, css, nothing } from 'lit';
import type { PropertyValues } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { endpoints } from '../api/client.js';
import { navigate } from '../router.js';
import '../components/sso-card.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';
import '../components/sso-spinner.js';

/**
 * `<recovery-codes-page>` — show one-time recovery codes once, right after the first 2FA setup
 * (SSO-MOCKUPS.md §7). Codes are stored hashed server-side; this is the only time they are readable.
 *
 * Copy / download / print are client-side. "Continue" stays disabled until the user confirms they
 * saved the codes (forced acknowledgement).
 */
@customElement('recovery-codes-page')
@localized()
export class RecoveryCodesPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.4rem 1.5rem;
        font: var(--sso-font-mono);
        font-size: 1.05rem;
        padding: var(--sso-space);
        border: 1px dashed var(--sso-color-border);
        border-radius: var(--sso-radius);
        margin: var(--sso-space) 0;
        user-select: all;
      }
      @media (max-width: 420px) {
        .grid {
          grid-template-columns: 1fr;
        }
      }
      .tools {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
      }
      .tools sso-button {
        flex: 1;
        min-width: 7rem;
      }
      .ack {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        margin: var(--sso-space) 0;
        cursor: pointer;
        color: var(--sso-color-fg);
      }
      .ack input {
        margin-top: 0.2rem;
      }
    `,
  ];

  @state() private codes?: string[];
  @state() private error = '';
  @state() private acked = false;
  /** Re-entrancy guard so rapid repeated Copy clicks don't fire overlapping clipboard writes/toasts. */
  private copying = false;
  @query('#ack') private ackBox?: HTMLInputElement;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    // Prefer the one-time codes captured during enrollment (shown once, never re-readable).
    const captured = this.flowContext?.takeRecoveryCodes() ?? [];
    if (captured.length) {
      this.codes = captured;
      return;
    }
    // Direct navigation (e.g. "regenerate") → ask the server for a fresh set.
    try {
      const r = await endpoints.mfaRecoveryRegenerate();
      this.codes = r.codes;
    } catch {
      this.error = msg('Could not generate recovery codes. Please try again.');
    }
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    // Codes may load asynchronously — focus the first action (Copy) once content has rendered.
    this.focusFirstControl();
  }

  private get text(): string {
    return (this.codes ?? []).join('\n');
  }

  private async copy(): Promise<void> {
    if (this.copying) return; // ignore rapid repeated clicks while a write is in flight
    this.copying = true;
    try {
      await navigator.clipboard.writeText(this.text);
      this.toast(msg('Recovery codes copied'), 'success');
    } catch {
      this.toast(msg('Could not copy to clipboard'), 'error');
    } finally {
      this.copying = false;
    }
  }

  private download(): void {
    const blob = new Blob([`${msg('Pinakes recovery codes')}\n\n${this.text}\n`], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pinakes-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  private print(): void {
    const w = window.open('', '_blank');
    if (!w) return;
    const items = (this.codes ?? []).map((c) => `<li>${c}</li>`).join('');
    w.document.write(
      `<title>${msg('Pinakes recovery codes')}</title>` +
        `<h1>${msg('Pinakes recovery codes')}</h1><ol style="font-family:monospace;font-size:16px">${items}</ol>`,
    );
    w.document.close();
    w.focus();
    w.print();
  }

  private onAck(): void {
    this.acked = this.ackBox?.checked ?? false;
  }

  private cont(): void {
    if (!this.acked) return;
    const returnUri = this.resolveReturnUri();
    if (returnUri) { this.redirect(returnUri); return; }
    navigate('/');
  }

  override render() {
    if (!this.codes && !this.error) {
      return html`<sso-card><sso-spinner aria-label=${msg('Loading')}></sso-spinner></sso-card>`;
    }
    return html`
      <sso-card>
        <h1>${msg('Save your recovery codes')}</h1>
        <p>
          ${msg(
            'Use one of these codes if you lose access to your passkey or authenticator app. Each code works once.',
          )}
        </p>
        ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
        ${this.codes
          ? html`
              <div class="grid" role="list" aria-label=${msg('Recovery codes')}>
                ${this.codes.map((c) => html`<span role="listitem">${c}</span>`)}
              </div>
              <div class="tools">
                <sso-button variant="secondary" @click=${this.copy}>${msg('Copy')}</sso-button>
                <sso-button variant="secondary" @click=${this.download}>${msg('Download')}</sso-button>
                <sso-button variant="secondary" @click=${this.print}>${msg('Print')}</sso-button>
              </div>
              <label class="ack">
                <input id="ack" type="checkbox" @change=${this.onAck} />
                ${msg('I have stored these codes in a safe place')}
              </label>
              <sso-button variant="primary" ?disabled=${!this.acked} @click=${this.cont}>
                ${msg('Continue')}
              </sso-button>
            `
          : nothing}
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'recovery-codes-page': RecoveryCodesPage;
  }
}
